// Generic cluster overview for super-admin engine monitoring.
// One template renders Pingora / BM-Engine / AD-Engine by switching on the
// `engineType` prop. The page polls `/cluster/:type` + `/cluster/:type/summary`
// on mount + every 30s (auto-refresh toggle).
//
// Rows with `ageSeconds > 86400` are hidden — those are zombie test heartbeats
// (the Step-1 leftover row in particular) that would otherwise inflate the
// server count and skew the cluster trend charts.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import DnsIcon from "@mui/icons-material/Dns";
import SpeedIcon from "@mui/icons-material/Speed";
import MemoryIcon from "@mui/icons-material/Memory";
import BoltIcon from "@mui/icons-material/Bolt";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  TOKENS,
  PageHeader,
  StatTile,
  SectionCard,
  StatusPill,
  EmptyState,
  RowMenu,
} from "../design";
import MetricChart from "./MetricChart";
import { getEngineConfig } from "./engineConfig";
import {
  getEngineCluster,
  getEngineClusterSummary,
  getEngineTimeseries,
} from "../../../../../services/engineMonitoringService";

const TIME_RANGES = [
  { value: "15m", label: "15m" },
  { value: "1h", label: "1h" },
  { value: "6h", label: "6h" },
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
];

const REFRESH_MS = 30_000;
const STALE_HIDE_SECONDS = 86_400; // 24h

const fmtUptime = (sec) => {
  if (sec == null || Number.isNaN(sec)) return "—";
  const s = Math.floor(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
};

const fmtPct = (v) => (v == null || Number.isNaN(v) ? "—" : `${Number(v).toFixed(1)}%`);
const fmtNum = (v, digits = 1) =>
  v == null || Number.isNaN(v) ? "—" : Number(v).toFixed(digits);

const statusFor = (row) => {
  if (row?.stale) return "stale";
  const h = (row?.healthStatus || "").toUpperCase();
  if (h === "HEALTHY") return "healthy";
  if (h === "DEGRADED") return "degraded";
  return "unknown";
};

const StatusForRow = ({ row }) => {
  const s = statusFor(row);
  if (s === "stale") {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: 24,
          px: "10px",
          borderRadius: 999,
          background: TOKENS.dangerBg,
          color: TOKENS.dangerInk,
          border: `1px solid ${TOKENS.dangerBorder}`,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: "50%", background: TOKENS.danger }}
        />
        Stale
      </Box>
    );
  }
  if (s === "healthy") return <StatusPill status="Active" />;
  if (s === "degraded") {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: 24,
          px: "10px",
          borderRadius: 999,
          background: TOKENS.warningBg,
          color: TOKENS.warningInk,
          border: `1px solid ${TOKENS.warningBorder}`,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: "50%", background: TOKENS.warning }}
        />
        Degraded
      </Box>
    );
  }
  return <StatusPill status="Pending" />;
};

const thSx = {
  padding: "10px 14px",
  borderBottom: `1px solid ${TOKENS.border}`,
  background: TOKENS.bgSunken,
  color: TOKENS.fg2,
  fontSize: 11.5,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};
const tdSx = {
  padding: "12px 14px",
  borderBottom: `1px solid ${TOKENS.borderSubtle}`,
  fontSize: 13,
  color: TOKENS.fg1,
};

const TimeRangeSwitcher = ({ value, onChange }) => (
  <Box
    sx={{
      display: "inline-flex",
      border: `1px solid ${TOKENS.border}`,
      borderRadius: `${TOKENS.radiusXs}px`,
      overflow: "hidden",
      background: TOKENS.bgSurface,
    }}
  >
    {TIME_RANGES.map((r) => {
      const active = r.value === value;
      return (
        <Box
          key={r.value}
          onClick={() => onChange(r.value)}
          sx={{
            cursor: "pointer",
            px: 1.25,
            py: 0.5,
            fontSize: 12,
            fontWeight: 600,
            color: active ? TOKENS.primaryInk : TOKENS.fg2,
            background: active ? TOKENS.primaryTint : "transparent",
            borderRight: `1px solid ${TOKENS.border}`,
            "&:last-of-type": { borderRight: "none" },
            "&:hover": { color: TOKENS.fg1 },
          }}
        >
          {r.label}
        </Box>
      );
    })}
  </Box>
);

const EngineClusterPage = ({ engineType }) => {
  const cfg = getEngineConfig(engineType);
  const navigate = useNavigate();

  const [engines, setEngines] = useState([]);
  const [summary, setSummary] = useState(null);
  const [timeseriesByEngine, setTimeseriesByEngine] = useState({});
  const [range, setRange] = useState("1h");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState(null);

  const visibleEngines = useMemo(
    () => engines.filter((e) => (e?.ageSeconds ?? 0) <= STALE_HIDE_SECONDS),
    [engines]
  );

  const longRange = range === "24h" || range === "7d";

  const fetchClusterAndSummary = useCallback(async () => {
    try {
      const [clusterRes, summaryRes] = await Promise.all([
        getEngineCluster(engineType),
        getEngineClusterSummary(engineType),
      ]);
      setEngines(clusterRes?.data?.engines || []);
      setSummary(summaryRes?.data || null);
      setError(null);
    } catch (e) {
      setError(e?.message || "Failed to load engine data");
    } finally {
      setLoading(false);
    }
  }, [engineType]);

  const fetchTimeseries = useCallback(
    async (engineList) => {
      if (!engineList || engineList.length === 0) {
        setTimeseriesByEngine({});
        return;
      }
      setChartLoading(true);
      try {
        const results = await Promise.all(
          engineList.map((e) =>
            getEngineTimeseries(e.id, {
              range,
              metrics: "cpu_pct,mem_pct,load_1m,requests_total",
            })
              .then((res) => ({ id: e.id, hostname: e.hostname, series: res?.data?.series || {} }))
              .catch(() => ({ id: e.id, hostname: e.hostname, series: {} }))
          )
        );
        const next = {};
        for (const r of results) next[r.id] = r;
        setTimeseriesByEngine(next);
      } finally {
        setChartLoading(false);
      }
    },
    [range]
  );

  useEffect(() => {
    let cancelled = false;
    let iv = null;
    (async () => {
      await fetchClusterAndSummary();
      if (cancelled) return;
      if (autoRefresh) {
        iv = setInterval(fetchClusterAndSummary, REFRESH_MS);
      }
    })();
    return () => {
      cancelled = true;
      if (iv) clearInterval(iv);
    };
  }, [fetchClusterAndSummary, autoRefresh]);

  useEffect(() => {
    fetchTimeseries(visibleEngines);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, visibleEngines.length]);

  const cpuSeries = useMemo(
    () =>
      visibleEngines.map((e, i) => ({
        label: e.hostname || e.ip || `engine ${i + 1}`,
        color: TOKENS.monoPalette[i % TOKENS.monoPalette.length][1],
        data: (timeseriesByEngine[e.id]?.series?.cpu_pct || []).map((p) => ({
          ts: p.ts,
          v: p.v,
        })),
      })),
    [visibleEngines, timeseriesByEngine]
  );

  const memSeries = useMemo(
    () =>
      visibleEngines.map((e, i) => ({
        label: e.hostname || e.ip || `engine ${i + 1}`,
        color: TOKENS.monoPalette[i % TOKENS.monoPalette.length][1],
        data: (timeseriesByEngine[e.id]?.series?.mem_pct || []).map((p) => ({
          ts: p.ts,
          v: p.v,
        })),
      })),
    [visibleEngines, timeseriesByEngine]
  );

  const loadSeries = useMemo(
    () =>
      visibleEngines.map((e, i) => ({
        label: e.hostname || e.ip || `engine ${i + 1}`,
        color: TOKENS.monoPalette[i % TOKENS.monoPalette.length][1],
        data: (timeseriesByEngine[e.id]?.series?.load_1m || []).map((p) => ({
          ts: p.ts,
          v: p.v,
        })),
      })),
    [visibleEngines, timeseriesByEngine]
  );

  const showRequestsChart = cfg.appCharts.some((c) => c.key === "requests_rate");
  const requestsSeries = useMemo(
    () =>
      visibleEngines.map((e, i) => ({
        label: e.hostname || e.ip || `engine ${i + 1}`,
        color: TOKENS.monoPalette[i % TOKENS.monoPalette.length][1],
        data: (timeseriesByEngine[e.id]?.series?.requests_rate || []).map((p) => ({
          ts: p.ts,
          v: p.v,
        })),
      })),
    [visibleEngines, timeseriesByEngine]
  );

  const healthyCount = summary?.healthyCount ?? 0;
  const staleCount = summary?.staleCount ?? 0;
  const engineCount = summary?.engineCount ?? engines.length;
  const subtitle = `${engineCount} server${engineCount === 1 ? "" : "s"} · ${healthyCount} healthy · ${staleCount} stale`;

  const headerActions = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <TimeRangeSwitcher value={range} onChange={setRange} />
      <FormControlLabel
        sx={{ ml: 1, color: TOKENS.fg2, "& .MuiFormControlLabel-label": { fontSize: 12.5 } }}
        control={
          <Switch
            size="small"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
        }
        label="Auto-refresh"
      />
      <Button
        onClick={fetchClusterAndSummary}
        startIcon={<RefreshIcon style={{ width: 16, height: 16 }} />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: 12.5,
          color: TOKENS.fg1,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusXs}px`,
          px: 1.5,
          py: 0.5,
          "&:hover": { background: TOKENS.bgSunken },
        }}
      >
        Refresh
      </Button>
    </Stack>
  );

  return (
    <Box>
      <Helmet title={`${cfg.displayName} cluster`} />
      <PageHeader title={`${cfg.displayName} cluster`} subtitle={subtitle} actions={headerActions} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<DnsIcon fontSize="small" />}
            iconBg={TOKENS.primaryTint}
            iconInk={TOKENS.primaryInk}
            value={`${healthyCount} / ${engineCount}`}
            label="Servers online"
            sub={staleCount > 0 ? `${staleCount} stale` : "All reporting"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<BoltIcon fontSize="small" />}
            iconBg="#FEF3C7"
            iconInk="#92400E"
            value={summary?.totalRequestsRate != null ? Number(summary.totalRequestsRate).toFixed(1) : "—"}
            label="Total req/s"
            sub={showRequestsChart ? "Aggregate across servers" : "Not exposed by this engine"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<SpeedIcon fontSize="small" />}
            iconBg={TOKENS.infoBg}
            iconInk={TOKENS.infoInk}
            value={fmtPct(summary?.avgCpuPct)}
            label="Avg CPU"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<MemoryIcon fontSize="small" />}
            iconBg={TOKENS.successBg}
            iconInk={TOKENS.successInk}
            value={fmtPct(summary?.avgMemPct)}
            label="Avg memory"
          />
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <SectionCard
          title="Cluster trends"
          description={
            chartLoading
              ? "Loading…"
              : `One line per server · ${range} window`
          }
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <MetricChart series={cpuSeries} height={200} unit="%" title="CPU %" longRange={longRange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <MetricChart series={memSeries} height={200} unit="%" title="Memory %" longRange={longRange} />
            </Grid>
            {showRequestsChart ? (
              <Grid item xs={12} md={6}>
                <MetricChart
                  series={requestsSeries}
                  height={200}
                  unit="/s"
                  title="Requests / sec"
                  longRange={longRange}
                />
              </Grid>
            ) : null}
            <Grid item xs={12} md={showRequestsChart ? 6 : 12}>
              <MetricChart series={loadSeries} height={200} title="Load avg (1m)" longRange={longRange} />
            </Grid>
          </Grid>
        </SectionCard>
      </Box>

      <SectionCard title="Servers" description={loading ? "Loading…" : `${visibleEngines.length} active server${visibleEngines.length === 1 ? "" : "s"}`}>
        {error ? (
          <Box
            sx={{
              p: 2,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.dangerBg,
              border: `1px solid ${TOKENS.dangerBorder}`,
              color: TOKENS.dangerInk,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Failed to load engine data. {error}</span>
            <Button
              size="small"
              onClick={fetchClusterAndSummary}
              sx={{ textTransform: "none", color: TOKENS.dangerInk, fontWeight: 600 }}
            >
              Retry
            </Button>
          </Box>
        ) : visibleEngines.length === 0 && !loading ? (
          <EmptyState
            icon={<DnsIcon />}
            title={`No ${cfg.displayName} servers reporting`}
            body="Heartbeats arrive every 30s. If a server is missing, check the heartbeat sender service on the host."
          />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Server</TableCell>
                  <TableCell sx={thSx}>IP</TableCell>
                  <TableCell sx={thSx}>Version</TableCell>
                  <TableCell sx={thSx} align="right">CPU%</TableCell>
                  <TableCell sx={thSx} align="right">Mem%</TableCell>
                  <TableCell sx={thSx} align="right">Load 1m</TableCell>
                  <TableCell sx={thSx}>Uptime</TableCell>
                  <TableCell sx={thSx}>Status</TableCell>
                  <TableCell sx={thSx} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleEngines.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/super/engines/${cfg.slug}/${encodeURIComponent(row.id)}`)}
                  >
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: TOKENS.fg1 }}>
                        {row.hostname || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Box component="span" sx={{ fontFamily: "monospace", fontSize: 12.5, color: TOKENS.fg2 }}>
                        {row.ip || "—"}
                      </Box>
                    </TableCell>
                    <TableCell sx={tdSx}>{row.version || "—"}</TableCell>
                    <TableCell sx={tdSx} align="right">{fmtPct(row.cpuUsage)}</TableCell>
                    <TableCell sx={tdSx} align="right">{fmtPct(row.memoryUsage)}</TableCell>
                    <TableCell sx={tdSx} align="right">{fmtNum(row.loadAvg1, 2)}</TableCell>
                    <TableCell sx={tdSx}>{fmtUptime(row.uptime)}</TableCell>
                    <TableCell sx={tdSx}><StatusForRow row={row} /></TableCell>
                    <TableCell sx={tdSx} align="right" onClick={(e) => e.stopPropagation()}>
                      <RowMenu
                        actions={[
                          {
                            key: "view",
                            label: "View details",
                            onClick: () =>
                              navigate(`/super/engines/${cfg.slug}/${encodeURIComponent(row.id)}`),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </SectionCard>
    </Box>
  );
};

export default EngineClusterPage;
